/* eslint-disable max-lines */
import type { Dispatch, FormEvent, ReactElement, SetStateAction, MouseEvent } from 'react';
import { useEffect, useRef, useState, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMedia, useEvent, useLocalStorage } from 'react-use';
import Fuse from 'fuse.js';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import type SimpleBar from 'simplebar-react';
import {
  StyledAirplaneIcon,
  StyledChevron,
  StyledCloseButton,
  StyledInput,
  StyledInputWrapper,
  StyledLabel,
  StyledListItem,
  StyledLocationSelector,
  StyledOverlayLabel,
  StyledPlaceholder,
  StyledResultList,
  StyledResultWrapper,
  StyledDivider,
  StyledClearButton,
} from './LocationSelector.styles';
import { Paragraph, ParagraphType } from '../../atoms/Paragraph/Paragraph';
import AppContext from '../../../utils/createContext';
import { Icon } from '../../atoms/Icon/Icon';
import { CurrentLocation } from '../CurrentLocation/CurrentLocation';
import { MediaQuery } from '../../../styles/mediaQuery';
import type { AirportType } from '../../../utils/types';
import { LocalStorageKey } from '../../../data/keys';
import useAlphabeticList from '../../../hooks/useAlphabeticList';
import { isConnected, isUnserved } from '../../../utils/checkFlightType';
import { isCurrent } from '../../../utils/checkAirport';
import useMount from '../../../hooks/useMount';
import useUnmount from '../../../hooks/useUnmount';

type LocationSelectorProps = {
  location: AirportType | Record<string, never>;
  setLocation: Dispatch<SetStateAction<AirportType | Record<string, unknown>>>;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  label: string;
  placeholder: string;
  overlayLabel: string;
  isDeparture?: boolean;
};

export function LocationSelector({
  location,
  setLocation,
  isDeparture = false,
  label,
  placeholder,
  overlayLabel,
  isOpen,
  setIsOpen,
}: LocationSelectorProps): ReactElement {
  const {
    airportsData,
    originAirport,
    destinationAirport,
    setDestinationAirport,
    nonstopChecked,
    showDisabledModal,
    setShowDisabledModal,
    setShowDisabledModalConnection,
    userLocationError,
  } = useContext(AppContext);
  const alphabeticList = useAlphabeticList(airportsData);
  const [query, setQuery] = useState('');
  const [isFocussed, setIsFocussed] = useState(false);
  const [isChosen, setIsChosen] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<Array<Fuse.FuseResult<AirportType>>>(
    [],
  );
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const [recentSearches, setRecentSearches]: [
    Array<AirportType>,
    Dispatch<SetStateAction<Array<AirportType>>>,
  ] = useLocalStorage(LocalStorageKey.RecentSearches, []);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<SimpleBar>(null);
  const isDesktop = useMedia(MediaQuery.Large);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    } else {
      inputRef.current?.blur();
    }
  }, [isOpen]);

  const fuse = new Fuse(airportsData, {
    keys: [
      'airport_name',
      'booking_widget_display_name',
      'iata_code',
      'federal_unit',
      'alt_search_names',
    ],
    minMatchCharLength: 3,
    includeScore: true,
    threshold: 0.2,
  });

  const handleSearchQuery = (searchTerm: string) => {
    if (!isOpen) {
      setIsOpen(true);
    }
    setQuery(searchTerm);
    if (searchTerm.length < 2) {
      setSearchSuggestions([]);
    }
    const results = fuse.search(searchTerm.toLowerCase());
    // .filter((result) => result.score && result.score < 0.03);
    setSearchSuggestions(results as Array<Fuse.FuseResult<AirportType>>);
  };

  const openModal = () => {
    if (!isOpen) {
      setIsOpen(true);
      setTimeout(() => {
        if (scrollRef.current) {
          disableBodyScroll(scrollRef.current.getScrollElement());
        }
      }, 0);
    }
  };

  const closeModal = () => {
    if (isOpen) {
      setIsOpen(false);
      setQuery('');
      setSearchSuggestions([]);
      clearAllBodyScrollLocks();
    }
  };

  const toggleModal = () => {
    if (!isOpen) {
      openModal();
    }
  };

  const onLocationChange = (airport: AirportType, disabled: boolean) => {
    if (airport.suspended || (disabled && !nonstopChecked)) {
      setShowDisabledModal(true);
    } else if (disabled && nonstopChecked) {
      setShowDisabledModalConnection(true);
    } else {
      setIsChosen(true);
      setQuery('');
      setSearchSuggestions([]);
      closeModal();
      setLocation(airport);
      const temporarySearchesArray = recentSearches?.filter(
        (locationItem) => locationItem.iata_code !== airport.iata_code,
      );
      if (temporarySearchesArray.length >= 3) {
        temporarySearchesArray.splice(2, 1);
      }
      setRecentSearches([airport, ...temporarySearchesArray]);
    }
  };

  const handleRemoveLocation = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    event.stopPropagation();
    if (isDeparture) {
      setDestinationAirport({});
    }
    setLocation({});
  };

  const handleMousedown = (event: Event) => {
    if (isOpen && !showDisabledModal && !wrapperRef.current?.contains(event.target as Node)) {
      closeModal();
    }
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (isOpen && !showDisabledModal && event.key === 'Escape') {
      closeModal();
    } else if (isOpen && isChosen) {
      setLocation({});
      setIsFocussed(true);
      inputRef.current?.focus();
    }
  };

  const handleBlur = () => {
    setIsFocussed(false);
  };

  useEvent('mousedown', (event) => handleMousedown(event), document);
  useEvent('keydown', (event) => handleKeydown(event as KeyboardEvent), document);

  useMount(() => {
    inputRef.current?.addEventListener('blur', () => {
      handleBlur();
    });
  });

  useUnmount(() => {
    inputRef.current?.removeEventListener('blur', () => {
      handleBlur();
    });
  });

  useEvent(
    'focus',
    () => {
      setIsFocussed(true);
    },
    inputRef.current,
  );

  useEvent(
    'focus',
    () => {
      setIsFocussed(true);
    },
    placeholderRef.current,
  );

  useEvent(
    'blur',
    () => {
      setIsFocussed(false);
    },
    placeholderRef.current,
  );

  const isDisabled = (airport: AirportType) => {
    return (
      (!isDeparture && isUnserved(originAirport, airport)) ||
      (isDeparture && isUnserved(destinationAirport, airport)) ||
      (nonstopChecked && isConnected(originAirport, airport)) ||
      isCurrent(originAirport, airport) ||
      isCurrent(destinationAirport, airport) ||
      airport.suspended ||
      !airport.operational
    );
  };

  return (
    <StyledLocationSelector $isOpen={isOpen}>
      {isOpen && !isDesktop && (
        <>
          <StyledOverlayLabel type={ParagraphType.Normal}>{overlayLabel}</StyledOverlayLabel>
          <StyledCloseButton onClick={closeModal}>
            <Icon.Close />
          </StyledCloseButton>
        </>
      )}
      <StyledLabel htmlFor={StyledInput}>{label}</StyledLabel>
      <div ref={wrapperRef}>
        <StyledInputWrapper
          aria-label={isDeparture ? 'select departing airport' : 'select arrival airport'}
          onClick={toggleModal}
          $isOpen={isOpen}
          $isFocussed={isFocussed}
        >
          <StyledAirplaneIcon $isDeparture={isDeparture} />
          {location?.iata_code ? (
            <StyledPlaceholder ref={placeholderRef}>
              {location?.booking_widget_display_name}
            </StyledPlaceholder>
          ) : (
            <StyledInput
              placeholder={placeholder}
              onChange={(event: FormEvent<HTMLInputElement>) =>
                handleSearchQuery(event.currentTarget.value)
              }
              value={query}
              ref={inputRef}
            />
          )}
          {location.airport_name && (
            <StyledClearButton
              type="button"
              onClick={(event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) =>
                handleRemoveLocation(event)
              }
            >
              <Icon.Close />
            </StyledClearButton>
          )}
          {!isOpen && !location.iata_code && (
            <StyledChevron onClick={toggleModal}>
              <Icon.ChevronDown />
            </StyledChevron>
          )}
        </StyledInputWrapper>
        {isOpen && (
          <StyledResultWrapper ref={scrollRef} autoHide={false}>
            {searchSuggestions.length > 0 ? (
              <StyledResultList $additionalSpace>
                {searchSuggestions.map(({ item }) => (
                  <StyledListItem
                    className={isDisabled(item) ? 'location-is-disabled' : ''}
                    key={item.iata_code}
                    onClick={() => onLocationChange(item, isDisabled(item))}
                  >
                    {item.booking_widget_display_name}
                    <Paragraph type={ParagraphType.Normal} as="span">
                      {item.iata_code}
                    </Paragraph>
                  </StyledListItem>
                ))}
              </StyledResultList>
            ) : (
              <div>
                {!userLocationError && (
                  <CurrentLocation onLocationChange={onLocationChange} isDisabled={isDisabled} />
                )}
                <StyledResultList $additionalSpace={userLocationError}>
                  <StyledDivider>
                    <FormattedMessage
                      id="navigation-recent-searches-label"
                      description="Recent searches copy"
                      defaultMessage="Recent searches"
                    />
                  </StyledDivider>
                  {recentSearches.map((item: AirportType) => (
                    <StyledListItem
                      className={isDisabled(item) ? 'location-is-disabled' : ''}
                      key={item.iata_code}
                      onClick={() => onLocationChange(item, isDisabled(item))}
                    >
                      {item.booking_widget_display_name}

                      <Paragraph type={ParagraphType.Small} as="span">
                        {item.iata_code}
                      </Paragraph>
                    </StyledListItem>
                  ))}
                  {alphabeticList.length > 0 &&
                    alphabeticList.map((alphabeticObject) => (
                      <div key={alphabeticObject.letter}>
                        <StyledDivider>{alphabeticObject.letter.toUpperCase()}</StyledDivider>
                        {alphabeticObject.airports.map((item: AirportType) => (
                          <StyledListItem
                            className={isDisabled(item) ? 'location-is-disabled' : ''}
                            key={item.iata_code}
                            onClick={() => onLocationChange(item, isDisabled(item))}
                          >
                            {item.booking_widget_display_name}

                            <Paragraph type={ParagraphType.Normal} as="span">
                              {item.iata_code}
                            </Paragraph>
                          </StyledListItem>
                        ))}
                      </div>
                    ))}
                </StyledResultList>
              </div>
            )}
          </StyledResultWrapper>
        )}
      </div>
    </StyledLocationSelector>
  );
}
